<template>
  <div class="weekly-planning-page -mx-4 -mt-4">
    <weekly-planning-header
      :week-label="currentWeekLabel"
      :week-range="currentWeekRange"
      :week-number="currentWeekNumber"
      :week-start-date="currentWeekStartDate"
      :week-end-date="currentWeekEndDate"
      :can-go-previous="canGoPreviousWeek"
      :can-go-next="canGoNextWeek"
      :selected-view="selectedView"
      :view-options="weeklyPlanningViewOptions"
      :subactivities-scope="subactivitiesScope"
      :reschedule-mode="rescheduleMode"
      :reschedule-count="rescheduleCount"
      :pending-reschedule-count="pendingRescheduleCount"
      :pending-reschedule-view-active="pendingRescheduleViewActive"
      :edited-subactivities="editedSubactivities"
      @previous-week="goToPreviousWeek"
      @next-week="goToNextWeek"
      @update:selected-view="selectedView = $event"
      @update:subactivities-scope="subactivitiesScope = $event"
      @enter-reschedule="enterRescheduleMode"
      @confirm-reschedule="confirmReschedule"
      @cancel-reschedule="cancelReschedule"
      @open-pending-reschedule="openPendingRescheduleView"
      @update:selected-discipline-id="selectedDisciplineId = $event"
    />

    <div class="p-4">
      <activity-filters
        v-if="project && project.id && selectedView !== 'man_hour_management'"
        ref="filtersRef"
        :projectId="project.id"
        @filters-updated="filtersUpdated"
      />

      <weekly-view-pending-reschedule
        v-if="pendingRescheduleViewActive"
        ref="pendingRescheduleView"
        :project-id="project && project.id ? project.id : null"
        :week-number="currentWeekNumber"
        :target-week-label="nextWeekLabel"
        :is-subactivity-mode="isSubactivityProgressMode"
        :reschedule-mode="rescheduleMode"
        @close="closePendingRescheduleView"
        @loaded="pendingRescheduleCount = $event.total"
      />
      <component
        v-else
        :is="currentViewComponent"
        :key="tableRefreshKey"
        v-bind="viewComponentProps"
        @update-rescheduled-activity="updateRescheduledActivity"
        @remove-rescheduled-activity="removeRescheduledActivity"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import WeeklyPlanningHeader from './weekly-planning-header.vue'
import WeeklyViewWbs from './weekly-view-wbs.vue'
import WeeklyViewGlobal from './weekly-view-global.vue'
import WeeklyViewDiscipline from './weekly-view-discipline.vue'
import WeeklyViewManager from './weekly-view-manager.vue'
import WeeklyViewManHourManagement from './weekly-view-man-hour-management.vue'
import WeeklyViewPendingReschedule from './weekly-view-pending-reschedule.vue'
import ActivityFilters from '·/components/activity-filters'
import { buildProjectWeeks, findCurrentWeekIndex } from './weeklyProjectWeeks.js'

const WEEKLY_PLANNING_VIEW_OPTIONS = [
  {
    key: 'wbs',
    labelKey: 'wbs_view',
    icon: 'layer-group',
    component: 'WeeklyViewWbs',
  },
  {
    key: 'global',
    labelKey: 'global_view',
    icon: 'list',
    component: 'WeeklyViewGlobal',
  },
  {
    key: 'discipline',
    labelKey: 'view_by_discipline',
    icon: 'tag',
    component: 'WeeklyViewDiscipline',
  },
  {
    key: 'man_hour_management',
    labelKey: 'man_hour_management',
    icon: 'user-clock',
    component: 'WeeklyViewManHourManagement',
    takeoffOnly: true,
  },
  {
    key: 'manager',
    labelKey: 'view_by_manager',
    icon: 'user',
    component: 'WeeklyViewManager',
  },
  {
    key: 'gantt',
    labelKey: 'display_gantt',
    icon: 'chart-gantt',
    routeName: 'project.activities.gantt',
  },
]

export default {
  name: 'project-activities-weekly',

  middleware: 'auth',

  hidePageTitle: true,
  menuOption: '2-2',

  components: {
    WeeklyPlanningHeader,
    WeeklyViewWbs,
    WeeklyViewGlobal,
    WeeklyViewDiscipline,
    WeeklyViewManager,
    WeeklyViewManHourManagement,
    WeeklyViewPendingReschedule,
    ActivityFilters,
  },

  data() {
    return {
      currentWeekIndex: 0,
      selectedView: WEEKLY_PLANNING_VIEW_OPTIONS[0].key,
      /** 'weekly' filtra por semana; 'general' trae todo (subactividades o actividades según modo). */
      subactivitiesScope: 'general',
      rescheduleMode: false,
      pendingRescheduleViewActive: false,
      pendingRescheduleCount: 0,
      /** Actividades o subactividades editadas en modo reprogramación. */
      editedSubactivities: [],
      tableRefreshKey: 0,
      isRescheduleSaving: false,
      generatedFilters: {},
      /** BoqRoot disciplina seleccionada (null = Todas). Solo takeoff. */
      selectedDisciplineId: null,
    }
  },

  computed: {
    ...mapGetters({
      project: 'app/project',
    }),

    projectWeeks() {
      if (!this.project) {
        return []
      }

      return buildProjectWeeks(
        this.project.current_start_date,
        this.project.current_end_date
      )
    },

    currentWeek() {
      if (!this.projectWeeks.length) {
        return null
      }

      const index = Math.min(this.currentWeekIndex, this.projectWeeks.length - 1)
      return this.projectWeeks[index]
    },

    currentWeekLabel() {
      return this.currentWeek ? this.currentWeek.label : ''
    },

    currentWeekRange() {
      return this.currentWeek ? this.currentWeek.range : ''
    },

    currentWeekStartDate() {
      return this.currentWeek ? this.currentWeek.start.format('YYYY-MM-DD') : null
    },

    currentWeekEndDate() {
      return this.currentWeek ? this.currentWeek.end.format('YYYY-MM-DD') : null
    },

    currentWeekNumber() {
      return this.currentWeek ? this.currentWeek.number : null
    },

    serializedProjectWeeks() {
      return (this.projectWeeks || []).map((week) => ({
        number: week.number,
        start: week.start.format('YYYY-MM-DD'),
        end: week.end.format('YYYY-MM-DD'),
      }))
    },

    canGoPreviousWeek() {
      return this.currentWeekIndex > 0
    },

    canGoNextWeek() {
      return this.projectWeeks.length > 0
        && this.currentWeekIndex < this.projectWeeks.length - 1
    },

    isSubactivityProgressMode() {
      const configs = this.project && this.project.configs
      return (configs && configs.progress_type_mode) === 'subactivity'
    },

    isTakeoffProgressMode() {
      const configs = this.project && this.project.configs
      return (configs && configs.progress_type_mode) === 'takeoff'
    },

    weeklyPlanningViewOptions() {
      let options = [...WEEKLY_PLANNING_VIEW_OPTIONS]

      if (!this.isTakeoffProgressMode) {
        options = options.filter((option) => !option.takeoffOnly)
      }

      if (this.project && this.project.can_collaborate) {
        options = options.filter((option) => option.key !== 'gantt')
      }

      return options
    },

    rescheduleCount() {
      return this.editedSubactivities.length
    },

    nextWeekLabel() {
      if (!this.projectWeeks.length) {
        return ''
      }
      const nextIndex = Math.min(this.currentWeekIndex + 1, this.projectWeeks.length - 1)
      const week = this.projectWeeks[nextIndex]
      return week ? `S${week.weekNumber}` : ''
    },

    currentViewComponent() {
      const viewOptions = this.weeklyPlanningViewOptions.filter((option) => option.component)
      const option = viewOptions.find((item) => item.key === this.selectedView)
        || viewOptions[0]

      return option ? option.component : 'WeeklyViewWbs'
    },

    viewComponentProps() {
      return {
        projectId: this.project && this.project.id ? this.project.id : null,
        weekStartDate: this.currentWeekStartDate,
        weekEndDate: this.currentWeekEndDate,
        weekNumber: this.currentWeekNumber,
        projectWeeks: this.serializedProjectWeeks,
        filterSubactivitiesByWeek: this.subactivitiesScope === 'weekly',
        rescheduleMode: this.rescheduleMode,
        editedSubactivities: this.editedSubactivities,
        activityFilters: this.generatedFilters,
        selectedDisciplineId: this.selectedDisciplineId,
      }
    },
  },

  metaInfo() {
    return {
      title: this.$t('week_schedule_title'),
    }
  },

  created() {
    this.syncWeeklyPageInfo()
    this.syncCurrentWeekIndex()
  },

  mounted() {
    this.$nextTick(() => {
      this.syncActivityFilters()
    })
  },

  watch: {
    '$route.name'(name) {
      if (name === 'project.activities.weekly') {
        this.syncWeeklyPageInfo()
      }
    },
    'project.id'(projectId) {
      if (projectId) {
        this.syncCurrentWeekIndex()
      }
    },
    'project.current_start_date'() {
      this.syncCurrentWeekIndex()
    },
    'project.current_end_date'() {
      this.syncCurrentWeekIndex()
    },
    rescheduleMode(active) {
      if (active) {
        this.loadPendingRescheduleCount()
        return
      }
      this.pendingRescheduleViewActive = false
      this.pendingRescheduleCount = 0
    },
  },

  methods: {
    syncActivityFilters() {
      this.generatedFilters = this.$refs.filtersRef
        ? this.$refs.filtersRef.generateFilters()
        : {}
    },

    filtersUpdated() {
      // Solo actualiza filtros: las tablas de actividades/subactividades reaccionan
      // vía prop activityFilters. No remonta grupos WBS/disciplina/encargado.
      this.syncActivityFilters()
      this.loadPendingRescheduleCount()
    },

    syncCurrentWeekIndex() {
      if (!this.projectWeeks.length) {
        this.currentWeekIndex = 0
        return
      }

      const index = findCurrentWeekIndex(this.projectWeeks)
      this.currentWeekIndex = Math.min(index, this.projectWeeks.length - 1)
    },

    goToPreviousWeek() {
      if (!this.canGoPreviousWeek) {
        return
      }
      this.currentWeekIndex -= 1
    },

    goToNextWeek() {
      if (!this.canGoNextWeek) {
        return
      }
      this.currentWeekIndex += 1
    },

    enterRescheduleMode() {
      this.rescheduleMode = true
    },

    openPendingRescheduleView() {
      if (!this.rescheduleMode) {
        return
      }
      this.pendingRescheduleViewActive = !this.pendingRescheduleViewActive
      if (this.pendingRescheduleViewActive && this.$refs.pendingRescheduleView) {
        this.$refs.pendingRescheduleView.refresh()
      } else if (!this.pendingRescheduleViewActive) {
        this.loadPendingRescheduleCount()
      }
    },

    closePendingRescheduleView() {
      this.pendingRescheduleViewActive = false
      this.loadPendingRescheduleCount()
    },

    async loadPendingRescheduleCount() {
      const projectId = this.project && this.project.id ? this.project.id : null
      if (!projectId || !this.rescheduleMode) {
        this.pendingRescheduleCount = 0
        return
      }

      const params = {
        ...this.generatedFilters,
        p: projectId,
        status: 9,
        per_page: 1,
        page: 1,
      }

      if (this.isSubactivityProgressMode) {
        params.filter_subactivities = 1
      } else {
        params.pg = 1
        delete params.per_page
        delete params.page
      }

      try {
        const { data } = await axios.get('/api/v2/activities', { params })
        if (data && data.success) {
          this.pendingRescheduleCount = data.total != null ? data.total : 0
        }
      } catch (error) {
        this.pendingRescheduleCount = 0
      }
    },

    confirmReschedule() {
      if (this.isRescheduleSaving || !this.editedSubactivities.length) {
        if (!this.editedSubactivities.length) {
          this.rescheduleMode = false
        }
        return
      }

      const projectId = this.project && this.project.id ? this.project.id : null
      if (!projectId) {
        this.$notify.error('error_rescheduling_activities')
        return
      }

      this.isRescheduleSaving = true
      axios.post('/api/v2/activities/reschedule', {
        project_id: projectId,
        activities: this.editedSubactivities,
      })
        .then(({ data }) => {
          if (data && data.success) {
            this.$notify.success('success_rescheduling_activities')
            this.editedSubactivities = []
            this.rescheduleMode = false
            this.pendingRescheduleViewActive = false
            this.tableRefreshKey += 1
            this.loadPendingRescheduleCount()
            return
          }
          this.$notify.error('error_rescheduling_activities')
        })
        .catch((error) => {
          console.error('[weekly] reschedule activities error:', error)
          this.$notify.error('error_rescheduling_activities')
        })
        .finally(() => {
          this.isRescheduleSaving = false
        })
    },

    cancelReschedule() {
      this.editedSubactivities = []
      this.rescheduleMode = false
      this.pendingRescheduleViewActive = false
      this.pendingRescheduleCount = 0
    },

    normalizeEditedSubactivity(entry) {
      return {
        id: entry.id,
        current_start_date: entry.current_start_date,
        current_end_date: entry.current_end_date,
        people: entry.people,
        manager_user_id: entry.manager_user_id,
        status: entry.status,
      }
    },

    updateRescheduledActivity(entry) {
      if (!entry || entry.id == null) {
        return
      }
      const normalized = this.normalizeEditedSubactivity(entry)
      const index = this.editedSubactivities.findIndex((item) => item.id === normalized.id)
      if (index === -1) {
        this.editedSubactivities.push(normalized)
        return
      }
      this.$set(this.editedSubactivities, index, normalized)
    },

    removeRescheduledActivity(activityId) {
      if (activityId == null) {
        return
      }
      const index = this.editedSubactivities.findIndex((item) => item.id === activityId)
      if (index !== -1) {
        this.editedSubactivities.splice(index, 1)
      }
    },

    syncWeeklyPageInfo() {
      this.$store.dispatch('app/updatePageInfo', {
        info: {
          title: '',
          subtitle: '',
          menuOption: '2-2',
          hidePageTitle: true,
        },
      })
    },
  },
}
</script>
